import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MasterDataSeriveService } from 'src/app/Services/master-data-serive.service';

@Injectable({
  providedIn: 'root'
})
export class ReportsV3ApiService {
  constructor(
    private masterService: MasterDataSeriveService,
    private http: HttpClient,
    @Inject('BASE_URL') private baseUrl: string
  ) {}

  getSellThroughReportV3(
    companyId: number,
    storeId: string,
    departmentId: number,
    categoryId: number,
    startDate?: string | null,
    endDate?: string | null,
    productionCycleId?: number
  ) {
    const user = this.masterService.getUserDetailsFromStorage();
    const params = new HttpParams()
      .set('companyId', companyId.toString())
      .set('storeId', storeId.toString())
      .set('departmentId', departmentId.toString())
      .set('categoryId', categoryId.toString())
      .set('userId', user.userId.toString())
      .set('startDate', startDate ?? 'null')
      .set('endDate', endDate ?? 'null')
      .set('productionCycleId', (productionCycleId ?? 0).toString());
    return this.http.get(`${this.baseUrl}Reports/v3/SellThrough`, { params });
  }

  getSellThroughRateV3(
    companyId: number,
    storeId: string,
    departmentId: number,
    categoryId: number,
    startDate?: string | null,
    endDate?: string | null,
    productionCycleId?: number
  ) {
    const user = this.masterService.getUserDetailsFromStorage();
    const params = new HttpParams()
      .set('companyId', companyId.toString())
      .set('storeId', storeId.toString())
      .set('departmentId', departmentId.toString())
      .set('categoryId', categoryId.toString())
      .set('userId', user.userId.toString())
      .set('startDate', startDate ?? 'null')
      .set('endDate', endDate ?? 'null')
      .set('productionCycleId', (productionCycleId ?? 0).toString());
    return this.http.get(`${this.baseUrl}Reports/v3/SellThrough/Rate`, { params });
  }

  getEmployeeTableV3(
    companyId: number,
    storeId: number,
    departmentId: string,
    categoryId: number,
    startDate: string | null,
    endDate: string | null,
    productionCycleId: number | null,
    goalIntervalID: number | null = null
  ) {
    const user = this.masterService.getUserDetailsFromStorage();
    let params = new HttpParams()
      .set('companyId', companyId.toString())
      .set('storeId', storeId.toString())
      .set('departmentId', departmentId.toString())
      .set('categoryId', categoryId.toString())
      .set('userId', user.userId.toString())
      .set('startDate', startDate ?? 'null')
      .set('endDate', endDate ?? 'null')
      .set('productionCycleId', (productionCycleId ?? 0).toString());
    // Only sent when the caller has an interval to identify the Hour period with (task 14062) —
    // GetEmployeeReportTableV3Query.GoalIntervalId is optional and every other period ignores it.
    if (goalIntervalID) {
      params = params.set('goalIntervalID', goalIntervalID.toString());
    }
    return this.http.get(`${this.baseUrl}Reports/v3/Employee/Table`, { params });
  }

  getEmployeeSummaryV3(
    companyId: number,
    storeId: number,
    departmentId: string,
    goalValueID: number,
    goalIntervalID: number,
    categoryId: number,
    startDate: string | null,
    endDate: string | null,
    productionCycleId: number | null
  ) {
    const user = this.masterService.getUserDetailsFromStorage();
    const params = new HttpParams()
      .set('companyId', companyId.toString())
      .set('storeId', storeId.toString())
      .set('departmentId', departmentId.toString())
      .set('goalValueID', goalValueID.toString())
      .set('goalIntervalID', goalIntervalID.toString())
      .set('categoryId', categoryId.toString())
      .set('userId', user.userId.toString())
      .set('startDate', startDate ?? 'null')
      .set('endDate', endDate ?? 'null')
      .set('productionCycleId', (productionCycleId ?? 0).toString());
    return this.http.get(`${this.baseUrl}Reports/v3/Employee/Summary`, { params });
  }

  getEmployeeTopPerformersV3(
    companyId: number,
    storeId: string,
    startDate: string | null,
    endDate: string | null,
    productionCycleId: number
  ) {
    const params = new HttpParams()
      .set('companyId', companyId.toString())
      .set('storeId', storeId.toString())
      .set('startDate', startDate ?? 'null')
      .set('endDate', endDate ?? 'null')
      .set('productionCycleId', productionCycleId.toString());
    return this.http.get(`${this.baseUrl}Reports/v3/Employee/TopPerformers`, { params });
  }

  getStoreTopPerformersV3(
    companyId: number,
    storeId: string,
    startDate: string | null,
    endDate: string | null,
    productionCycleId: number
  ) {
    const params = new HttpParams()
      .set('companyId', companyId.toString())
      .set('storeId', storeId.toString())
      .set('startDate', startDate ?? 'null')
      .set('endDate', endDate ?? 'null')
      .set('productionCycleId', productionCycleId.toString());
    return this.http.get(`${this.baseUrl}Reports/v3/Store/TopPerformers`, { params });
  }

  getSalePerProducerReportV3(
    companyId: number,
    storeIds: string,
    departmentIds: string,
    categoryIds: string,
    userId: number,
    startDate: string | null,
    endDate: string | null,
    productionCycleId: number
  ) {
    const params = new HttpParams()
      .set('CompanyId', companyId.toString())
      .set('StoreIds', storeIds)
      .set('DepartmentIds', departmentIds)
      .set('CategoryIds', categoryIds)
      .set('UserId', userId.toString())
      .set('StartDate', startDate ?? 'null')
      .set('EndDate', endDate ?? 'null')
      .set('ProductionCycleId', productionCycleId.toString());
    return this.http.get(`${this.baseUrl}Reports/v3/SalePerProducer`, { params });
  }

  getProductionOverviewDashboardV3(
    companyId: number,
    storeId: number,
    startDate: string | null,
    endDate: string | null,
    productionCycleId: number,
    departmentId?: number
  ) {
    let params = new HttpParams()
      .set('companyId', companyId.toString())
      .set('storeId', storeId.toString())
      .set('startDate', startDate ?? 'null')
      .set('endDate', endDate ?? 'null')
      .set('productionCycleId', productionCycleId.toString());
    // GetProductionOverviewDashboardV3Query.DepartmentId is optional (0/absent = all
    // departments) — only sent when the caller actually has a department to filter by.
    if (departmentId) {
      params = params.set('departmentId', departmentId.toString());
    }
    return this.http.get(`${this.baseUrl}Reports/v3/ProductionOverview/Dashboard`, { params });
  }

  getProductionOverviewTableV3(
    companyId: number,
    storeId: number,
    startDate: string | null,
    endDate: string | null,
    productionCycleId: number,
    departmentId?: number
  ) {
    let params = new HttpParams()
      .set('companyId', companyId.toString())
      .set('storeId', storeId.toString())
      .set('startDate', startDate ?? 'null')
      .set('endDate', endDate ?? 'null')
      .set('productionCycleId', productionCycleId.toString());
    if (departmentId) {
      params = params.set('departmentId', departmentId.toString());
    }
    return this.http.get(`${this.baseUrl}Reports/v3/ProductionOverview/Table`, { params });
  }

  // storeId is passed as a separate param (private setter — not part of [AsParameters] block)
  getProductionOverviewAdminDashboardV3(
    companyId: number,
    storeId: number,
    startDate: string | null,
    endDate: string | null,
    productionCycleId: number
  ) {
    const params = new HttpParams()
      .set('companyId', companyId.toString())
      .set('startDate', startDate ?? 'null')
      .set('endDate', endDate ?? 'null')
      .set('productionCycleId', productionCycleId.toString())
      .set('storeId', storeId.toString());
    return this.http.get(`${this.baseUrl}Reports/v3/ProductionOverview/Admin/Dashboard`, { params });
  }

  getProductionOverviewAdminTableV3(
    companyId: number,
    storeId: number,
    isActive: boolean,
    startDate: string | null,
    endDate: string | null,
    productionCycleId: number
  ) {
    const params = new HttpParams()
      .set('companyId', companyId.toString())
      .set('storeId', storeId.toString())
      .set('isActive', isActive.toString())
      .set('startDate', startDate ?? 'null')
      .set('endDate', endDate ?? 'null')
      .set('productionCycleId', productionCycleId.toString());
    return this.http.get(`${this.baseUrl}Reports/v3/ProductionOverview/Admin/Table`, { params });
  }
}
